import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Search, Download, Settings2, Pencil, Trash2, Check, X, GripVertical, CircleCheck, CircleDashed } from "lucide-react";
import { StatCard, Modal, TextInput, PrimaryBtn, GhostBtn, EmptyState } from "./CrmUI";
import {
    getOnbConfig, saveOnbConfig, getOnboarding, getOnboardingStats,
    createOnboarding, updateOnboarding, deleteOnboarding,
} from "./crmApi";

const StatusBadge = ({ status }) => (
    status === "completed"
        ? <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#0B6B3A]/12 text-[#0B6B3A]" data-testid="onb-status-completed"><CircleCheck size={12} /> Completed</span>
        : <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#B5651D]/12 text-[#B5651D]" data-testid="onb-status-pending"><CircleDashed size={12} /> Pending</span>
);

// ── Add new onboarding client ────────────────────────────────────────────────
const AddClientModal = ({ onClose, onSaved, config }) => {
    const items = config?.items || [];
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [reqs, setReqs] = useState(() => Object.fromEntries(items.map((it) => [it.id, !!it.required])));
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggle = (id) => setReqs((r) => ({ ...r, [id]: !r[id] }));
    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required."); return; }
        setSaving(true);
        try { await createOnboarding({ ...form, requirements: reqs }); toast.success("Client added to onboarding."); onSaved(); onClose(); }
        catch { toast.error("Could not add client."); }
        finally { setSaving(false); }
    };
    const reqCount = items.filter((it) => reqs[it.id]).length;
    return (
        <Modal open onClose={onClose} title="Add new client" subtitle="Choose which checklist items are required for this client" maxWidth="max-w-2xl" testId="onb-add-modal">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <TextInput label="Name *" value={form.name} onChange={(v) => set("name", v)} testId="onb-add-name" />
                <TextInput label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="onb-add-email" />
                <TextInput label="Mobile" value={form.phone} onChange={(v) => set("phone", v)} testId="onb-add-phone" />
            </div>
            <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body">Checklist for this client</span>
                    <span className="text-[11px] text-oth-ink3 font-body" data-testid="onb-add-reqcount">{reqCount} required</span>
                </div>
                {items.length === 0 ? (
                    <div className="text-sm italic text-oth-ink3 font-body">No checklist items defined yet — add them in Settings first.</div>
                ) : (
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto thin-scroll" data-testid="onb-add-reqs">
                        {items.map((it) => (
                            <div key={it.id} className="flex items-center justify-between rounded-2xl border border-oth-line/60 bg-white px-4 py-2.5" data-testid={`onb-add-req-${it.id}`}>
                                <span className={`font-body text-sm ${reqs[it.id] ? "text-oth-ink" : "text-oth-ink3"}`}>{it.label}</span>
                                <button onClick={() => toggle(it.id)} className={`px-3 py-1.5 rounded-full text-xs font-body border whitespace-nowrap transition-colors ${reqs[it.id] ? "bg-[#B5651D] text-white border-[#B5651D]" : "bg-white text-oth-ink2 border-oth-line/70"}`} data-testid={`onb-add-toggle-${it.id}`}>{reqs[it.id] ? "Required" : "Not required"}</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="onb-add-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="onb-add-save">{saving ? "Adding…" : "Add client"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

// ── Checklist modal (per client) ─────────────────────────────────────────────
const ChecklistModal = ({ onClose, record, items, onSaved }) => {
    const [checklist, setChecklist] = useState(() => ({ ...(record.checklist || {}) }));
    const [reqs, setReqs] = useState(() => Object.fromEntries(items.map((it) => [it.id, record.requirements?.[it.id] ?? !!it.required])));
    const [editReq, setEditReq] = useState(false);
    const [saving, setSaving] = useState(false);

    const cell = (id) => checklist[id] || { done: false, note: "" };
    const setDone = (id, done) => setChecklist((c) => ({ ...c, [id]: { ...cell(id), done } }));
    const setNote = (id, note) => setChecklist((c) => ({ ...c, [id]: { ...cell(id), note } }));
    const toggleReq = (id) => setReqs((r) => ({ ...r, [id]: !r[id] }));

    const activeItems = items.filter((it) => reqs[it.id]);
    const missing = activeItems.filter((it) => !cell(it.id).done);
    const status = missing.length === 0 ? "completed" : "pending";

    const save = async () => {
        setSaving(true);
        try { await updateOnboarding(record.id, { checklist, requirements: reqs }); toast.success("Checklist saved."); onSaved(); onClose(); }
        catch { toast.error("Could not save."); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title={record.name} subtitle={record.email || record.phone || "Onboarding checklist"} maxWidth="max-w-2xl" testId="onb-checklist-modal">
            {/* Status / what's missing */}
            <div className={`rounded-2xl border p-4 mb-4 ${status === "completed" ? "border-[#A7D7BE] bg-[#F0F8F3]" : "border-[#E7C9A8] bg-[#FCF6EE]"}`} data-testid="onb-checklist-status">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <StatusBadge status={status} />
                        <span className="font-body text-sm text-oth-ink2">{activeItems.length - missing.length}/{activeItems.length} required complete</span>
                    </div>
                    <button onClick={() => setEditReq((v) => !v)} className="text-[12px] font-body text-oth-blue hover:underline" data-testid="onb-edit-reqs-toggle">{editReq ? "Done editing" : "Edit required items"}</button>
                </div>
                {!editReq && missing.length > 0 && (
                    <div className="mt-2 text-[13px] font-body text-[#8a4f17]" data-testid="onb-missing">
                        <span className="font-semibold">Missing:</span> {missing.map((m) => m.label).join(", ")}
                    </div>
                )}
            </div>

            {editReq ? (
                <div className="space-y-2 max-h-[46vh] overflow-y-auto thin-scroll" data-testid="onb-edit-reqs">
                    <div className="text-[12px] text-oth-ink3 font-body mb-1">Turn items on/off for this client. &quot;Not required&quot; items are hidden from the checklist.</div>
                    {items.length === 0 && <div className="text-sm italic text-oth-ink3 font-body">No checklist items yet — add some in Settings.</div>}
                    {items.map((it) => (
                        <div key={it.id} className="flex items-center justify-between rounded-2xl border border-oth-line/60 bg-white px-4 py-2.5" data-testid={`onb-editreq-${it.id}`}>
                            <span className={`font-body text-sm ${reqs[it.id] ? "text-oth-ink" : "text-oth-ink3"}`}>{it.label}</span>
                            <button onClick={() => toggleReq(it.id)} className={`px-3 py-1.5 rounded-full text-xs font-body border whitespace-nowrap transition-colors ${reqs[it.id] ? "bg-[#B5651D] text-white border-[#B5651D]" : "bg-white text-oth-ink2 border-oth-line/70"}`} data-testid={`onb-editreq-toggle-${it.id}`}>{reqs[it.id] ? "Required" : "Not required"}</button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="space-y-2 max-h-[46vh] overflow-y-auto thin-scroll" data-testid="onb-checklist-items">
                    {activeItems.length === 0 && <div className="text-sm italic text-oth-ink3 font-body">No required items for this client. Use &quot;Edit required items&quot; to add some.</div>}
                    {activeItems.map((it) => {
                        const c = cell(it.id);
                        return (
                            <div key={it.id} className="rounded-2xl border border-oth-line/60 bg-white p-3" data-testid={`onb-item-${it.id}`}>
                                <div className="flex items-start gap-3">
                                    <button onClick={() => setDone(it.id, !c.done)} className={`mt-0.5 w-6 h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors ${c.done ? "bg-[#0B6B3A] border-[#0B6B3A] text-white" : "bg-white border-oth-line hover:border-oth-ink"}`} data-testid={`onb-check-${it.id}`}>
                                        {c.done && <Check size={15} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <span className={`font-body text-sm ${c.done ? "text-oth-ink3 line-through" : "text-oth-ink"}`}>{it.label}</span>
                                        <input value={c.note} onChange={(e) => setNote(it.id, e.target.value)} placeholder="Add a note (e.g. doc number, remark)…" className="mt-2 w-full bg-[#FAFAF8] border border-oth-line/60 rounded-lg px-3 py-1.5 font-body text-[13px] focus:outline-none focus:border-oth-blue" data-testid={`onb-note-${it.id}`} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="onb-checklist-cancel">Close</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="onb-checklist-save">{saving ? "Saving…" : "Save"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

// ── Settings: manage master checklist items ─────────────────────────────────
const SettingsModal = ({ onClose, config, onSaved }) => {
    const [items, setItems] = useState(() => [...(config.items || [])]);
    const [saving, setSaving] = useState(false);

    const add = () => setItems((arr) => [...arr, { id: `tmp_${Date.now()}`, label: "", required: true, order: arr.length }]);
    const upd = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const del = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

    const save = async () => {
        const cleaned = items.filter((it) => (it.label || "").trim());
        setSaving(true);
        try { const saved = await saveOnbConfig({ items: cleaned }); onSaved(saved); toast.success("Checklist updated."); onClose(); }
        catch { toast.error("Could not save checklist."); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title="Onboarding checklist" subtitle="Add the items every new client must complete" maxWidth="max-w-2xl" testId="onb-settings-modal">
            <div className="space-y-2 max-h-[52vh] overflow-y-auto thin-scroll">
                {items.map((it, i) => (
                    <div key={it.id} className="flex items-center gap-2" data-testid={`onb-setting-item-${i}`}>
                        <GripVertical size={15} className="text-oth-ink3 shrink-0" />
                        <input value={it.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Checklist item label" className="flex-1 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid={`onb-setting-label-${i}`} />
                        <button onClick={() => upd(i, { required: !it.required })} className={`px-3 py-2 rounded-xl text-xs font-body border whitespace-nowrap transition-colors ${it.required ? "bg-[#B5651D] text-white border-[#B5651D]" : "bg-white text-oth-ink2 border-oth-line/70"}`} data-testid={`onb-setting-required-${i}`}>{it.required ? "Required" : "Optional"}</button>
                        <button onClick={() => del(i)} className="w-9 h-9 rounded-xl flex items-center justify-center text-oth-ink3 hover:text-red-600 shrink-0" data-testid={`onb-setting-del-${i}`}><Trash2 size={15} /></button>
                    </div>
                ))}
                {items.length === 0 && <div className="text-sm italic text-oth-ink3 font-body">No items yet. Add your first checklist item.</div>}
            </div>
            <button onClick={add} className="mt-3 px-4 py-2 rounded-full border border-oth-line/70 text-sm font-body text-oth-ink2 hover:bg-[#F5F5F2] inline-flex items-center gap-1.5" data-testid="onb-setting-add"><Plus size={14} /> Add item</button>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="onb-settings-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="onb-settings-save">{saving ? "Saving…" : "Save checklist"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

export const OnboardingCRM = () => {
    const [records, setRecords] = useState(null);
    const [config, setConfig] = useState(null);
    const [stats, setStats] = useState(null);
    const [query, setQuery] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [active, setActive] = useState(null);

    const reload = async () => {
        const [r, c, s] = await Promise.all([getOnboarding(), getOnbConfig(), getOnboardingStats()]);
        setRecords(r); setConfig(c); setStats(s);
    };
    useEffect(() => {
        (async () => {
            try {
                const [r, c, s] = await Promise.all([getOnboarding(), getOnbConfig(), getOnboardingStats()]);
                setRecords(r); setConfig(c); setStats(s);
            } catch { toast.error("Could not load onboarding."); }
        })();
    }, []);

    const rows = useMemo(() => {
        if (!records) return [];
        const q = query.trim().toLowerCase();
        return q ? records.filter((r) => [r.name, r.email, r.phone].join(" ").toLowerCase().includes(q)) : records;
    }, [records, query]);

    const doDelete = async (r) => {
        if (!window.confirm(`Remove "${r.name}" from onboarding?`)) return;
        try { await deleteOnboarding(r.id); toast.success("Removed."); reload(); }
        catch { toast.error("Could not remove."); }
    };

    const exportCsv = () => {
        const items = config?.items || [];
        const reqOf = (r, it) => (r.requirements?.[it.id] ?? !!it.required);
        const head = ["Name", "Email", "Phone", "Status", "Required done", "Required total", ...items.map((i) => i.label)];
        const data = rows.map((r) => [
            r.name, r.email, r.phone, r.status, r.required_done, r.required_total,
            ...items.map((i) => (reqOf(r, i) ? ((r.checklist || {})[i.id]?.done ? "Yes" : "No") : "—")),
        ]);
        const csv = [head, ...data].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `onboarding-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(a.href);
    };

    if (!records || !config) return <div className="font-editorial italic text-oth-ink3" data-testid="onboarding-loading">Loading onboarding…</div>;

    return (
        <div data-testid="onboarding-crm">
            <div className="grid grid-cols-3 gap-3 mb-6">
                <StatCard label="Total clients" value={stats?.total ?? 0} testId="onb-stat-total" />
                <StatCard label="Completed" value={stats?.completed ?? 0} accent="#0B6B3A" testId="onb-stat-completed" />
                <StatCard label="Pending" value={stats?.pending ?? 0} accent="#B5651D" testId="onb-stat-pending" />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-oth-ink3" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search clients…" className="w-full pl-9 pr-3 py-2.5 bg-white border border-oth-line/70 rounded-xl font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="onb-search" />
                </div>
                <GhostBtn onClick={exportCsv} testId="onb-export"><span className="inline-flex items-center gap-1.5"><Download size={14} /> Export</span></GhostBtn>
                <GhostBtn onClick={() => setSettingsOpen(true)} testId="onb-settings"><span className="inline-flex items-center gap-1.5"><Settings2 size={14} /> Settings</span></GhostBtn>
                <PrimaryBtn onClick={() => setAddOpen(true)} testId="onb-add"><Plus size={14} /> Add new client</PrimaryBtn>
            </div>

            {rows.length === 0 ? (
                <EmptyState title="No clients onboarding yet" body="Add a new client to start their checklist, and define the required documents in Settings." testId="onb-empty" />
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-x-auto">
                    <div className="min-w-[820px]">
                        <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-[#EFEFEA] text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body">
                            <div className="col-span-3">Name</div>
                            <div className="col-span-3">Contact</div>
                            <div className="col-span-2">Progress</div>
                            <div className="col-span-2">Status</div>
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {rows.map((r, i) => (
                            <motion.div key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.015, 0.3) }}
                                className="grid grid-cols-12 gap-3 px-5 py-3.5 border-t border-oth-line/60 text-sm font-body items-center hover:bg-[#F5F5F2]/60 transition-colors" data-testid={`onb-row-${r.id}`}>
                                <button onClick={() => setActive(r)} className="col-span-3 text-left font-medium text-oth-ink hover:text-oth-blue truncate" data-testid={`onb-name-${r.id}`}>{r.name}</button>
                                <div className="col-span-3 min-w-0">
                                    {r.email ? <span className="block truncate text-oth-ink2">{r.email}</span> : <span className="text-oth-ink3 italic text-xs">no email</span>}
                                    {r.phone ? <span className="block text-[12px] text-oth-ink3">{r.phone}</span> : null}
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-1.5 rounded-full bg-[#ECEBE7] overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${r.required_total ? (r.required_done / r.required_total) * 100 : 0}%`, background: r.status === "completed" ? "#0B6B3A" : "#B5651D" }} />
                                        </div>
                                        <span className="text-[11px] text-oth-ink3 whitespace-nowrap">{r.required_done}/{r.required_total}</span>
                                    </div>
                                </div>
                                <div className="col-span-2"><StatusBadge status={r.status} /></div>
                                <div className="col-span-2 flex items-center justify-end gap-0.5">
                                    <button onClick={() => setActive(r)} title="Open checklist" className="w-8 h-8 rounded-lg flex items-center justify-center text-oth-ink2 hover:text-oth-ink hover:bg-[#EFEFEA]" data-testid={`onb-open-${r.id}`}><Pencil size={15} /></button>
                                    <button onClick={() => doDelete(r)} title="Delete" className="w-8 h-8 rounded-lg flex items-center justify-center text-oth-ink3 hover:text-red-600 hover:bg-red-50" data-testid={`onb-delete-${r.id}`}><Trash2 size={15} /></button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {addOpen && <AddClientModal onClose={() => setAddOpen(false)} onSaved={reload} config={config} />}
            {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} config={config} onSaved={(c) => { setConfig(c); reload(); }} />}
            {active && <ChecklistModal key={active.id} onClose={() => setActive(null)} record={active} items={config.items || []} onSaved={reload} />}
        </div>
    );
};
